import { useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import useI18n from '../../../../lib/useI18n';

import { normalizeInitialValue } from '../utils';
import { generateIcons } from './toolbar';

export const useEditorValues = ({
  _value,
  form,
  name,
  onBlur,
  onChange,
  onFocus,
  placeholder,
}) => {
  const [value, setValue] = useState(_value);
  const [isActive, setIsActive] = useState(false);
  const quillRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const [showPlaceholder, setShowPlaceholder] = useState(
    Boolean(!_value && placeholder)
  );
  const initialized = useRef(false);
  const { translate } = useI18n('app.form.editor');

  const handleOnChange = (_value, _, source, { getText, getSemanticHTML }) => {
    // prevent empty paragraph to appear as non-empty value
    const normalizedValue =
      getText()?.trim()?.length > 0
        ? getSemanticHTML().replaceAll(/((?:&nbsp;)*)&nbsp;/g, '$1 ')
        : '';

    setValue(normalizedValue);

    // when we receive external data the editor will format it,
    // making field effectively dirty, so we have to reset the form with the
    // formatted value. This is done only if no manual changes have been made:
    // e.g. loading initial values, reseting the form..
    if (normalizedValue && form && source === 'api') {
      normalizeInitialValue({ form, value: normalizedValue, name });
    } else {
      onChange && onChange(normalizedValue);
    }
  };

  if (!initialized.current) {
    generateIcons({ Quill: ReactQuill.Quill, translate });
    initialized.current = true;
  }

  useEffect(() => {
    // if showPlaceholder changes value to false, we want to focus
    // on quill editor if active
    if (!showPlaceholder && isActive) {
      const quill = quillRef.current.getEditor();
      quill.focus();
    }
  }, [showPlaceholder, isActive]);

  useEffect(() => {
    // hide placeholder on focus
    isActive && setShowPlaceholder(false);
  }, [isActive]);

  useEffect(() => {
    // hide placeholder on programatic value change
    // e.g. fill values from profile/draft
    setShowPlaceholder(Boolean(!isActive && !_value && placeholder));
  }, [_value, placeholder, isActive]);

  const handleOnBlur = (range) => {
    setIsActive(false);
    if (
      !quillRef.current?.getEditor().container.contains(document.activeElement)
    ) {
      setIsExpanded(false);
    }
    onBlur?.(range);
    document.body.click();
  };

  const handleOnFocus = () => {
    setIsActive(true);
    setIsExpanded(true);
    onFocus?.();
  };
  return {
    handleOnBlur,
    handleOnChange,
    handleOnFocus,
    isActive,
    isExpanded,
    quillRef,
    setIsActive,
    setIsExpanded,
    showPlaceholder,
    value,
  };
};
